import { Owned } from './named';

export interface IMutateEventSink<T> {
    added?: (...items: T[]) => void;
    removed?: (...items: T[]) => void;
}

/*
    Ownedset is a specialized set which adds owned objects and sets its owner
    when items are added/deleted
*/
export class OwnedSet<T extends Owned> extends Set<T> {

    private _owner?: Owned;
    private _mutate?: IMutateEventSink<T>;

    /*
        Constructor only work in ES6
    */
    public constructor(owner?: Owned, mutate?: IMutateEventSink<T>) {
        super();
        this._owner = owner;
        this._mutate = mutate;
        Object.setPrototypeOf(this, OwnedSet.prototype);
    }

    /*
        Use create api to create for ES5
    */
    public static create<T extends Owned>(owner?: Owned, mutate?: IMutateEventSink<T>): OwnedSet<T> {
        const inst = new Set();
        Object.setPrototypeOf(inst, OwnedSet.prototype);
        const ownedset = inst as OwnedSet<T>;
        ownedset._owner = owner;
        ownedset._mutate = mutate;
        return ownedset;
    }

    public add(value: T, own: boolean = true): this {
        super.add(value);
        if (own) {
            this.own(value);
        }
        this.added(value);
        return this;
    }

    // eslint-disable-next-line
    public delete(value: T): boolean {
        if (super.delete(value)) {
            value['_owner'] = undefined;
            this.deleted(value);
            return true;
        }

        return false;
    }

    public find(predicate: (this: void, value: T) => boolean): T | undefined {
        // for of loops not supported in ES5. It is a pain to write all the boilerplate
        const iterator = this.values();
        let item = iterator.next();
        while (!item.done) {
            if (predicate(item.value)) {
                return item.value;
            }
            item = iterator.next();
        }
        return undefined;
    }

    public filter(predicate: (this: void, value: T) => boolean): OwnedSet<T> | undefined {
        // for of loops not supported in ES5. It is a pain to write all the boilerplate
        const iterator = this.values();
        let item = iterator.next();
        const result = OwnedSet.create<T>(this._owner);
        while (!item.done) {
            if (predicate(item.value)) {
                result.add(item.value);
            }
            item = iterator.next();
        }
        return result;
    }

    public clear(): void {
        const items: T[] = this._mutate ? Array.from(this.values()) : [];
        super.clear();
        this.forEach((owned) => {
            this.disown(owned);
        });

        this.deleted(...items);
    }

    private own(value: T): void {
        value['_owner'] = this._owner;
    }

    private disown(value: T): void {
        value['_owner'] = undefined;
    }

    private added(...items: T[]): void {
        if (this._mutate && this._mutate.added && items.length > 0) {
            this._mutate.added(...items);
        }
    }

    private deleted(...items: T[]): void {
        if (this._mutate && this._mutate.removed && items.length > 0) {
            this._mutate.removed(...items);
        }
    }
}
